<template>
  <div ref="reference" class="relative w-full">
    <Autocomplete :size="size" :disabled="disabled" @remove="removeAt">
      <template #selection="{ activeIndex }">
        <Chip
          v-for="(entity, index) in modelValue"
          :key="entity.id"
          :size="chipSize"
          :selected="index === activeIndex"
          :src="isEmail(entity) ? undefined : entity.avatarUrl || undefined"
          :icon="isEmail(entity) ? SmallGlobeIcon : undefined"
          @remove="removeAt(index)"
        >
          {{ entity.name }}
        </Chip>
      </template>
      <template #input="{ id }">
        <input
          :id="id"
          v-model="query"
          class="w-full"
          placeholder="Add an email or name"
          :disabled="disabled"
          @focus="handleFocus"
          @keydown="handleKeydown"
          @paste="handlePaste"
        />
      </template>
    </Autocomplete>

    <Teleport to="body">
      <Transition v-bind="transition">
        <div
          v-if="showResults && (loading || users.length || canAddEmail)"
          ref="floating"
          class="z-[999]"
          :style="floatingStyles"
        >
          <Menu class="max-h-[280px] overflow-y-auto" @scroll="handleScroll">
            <Menu.Section v-if="users.length"> Suggested </Menu.Section>

            <template v-if="loading && !users.length">
              <div
                v-for="n in 3"
                :key="n"
                class="flex items-center gap-[10px] px-[10px] py-[4px]"
              >
                <Skeleton class="size-7 shrink-0 rounded-full" />
                <div class="flex flex-1 flex-col gap-[4px]">
                  <Skeleton class="h-[12px] w-1/2 rounded-[4px]" />
                  <Skeleton class="h-[10px] w-2/3 rounded-[4px]" />
                </div>
              </div>
            </template>

            <Menu.Item
              v-for="entity in users"
              :key="entity.id"
              size="large"
              :avatar="entity.avatarUrl || undefined"
              :icon="ProfileIcon"
              @click="handleSelect(entity)"
            >
              <span class="flex min-w-0 items-center gap-[8px]">
                <span class="min-w-0 truncate">{{ entity.name }}</span>
                <Pill v-if="isYou(entity)" class="shrink-0">You</Pill>
              </span>
              <template v-if="getSecondaryText(entity)" #description>
                {{ getSecondaryText(entity) }}
              </template>
            </Menu.Item>

            <Menu.Item
              v-if="canAddEmail"
              size="large"
              :icon="SmallGlobeIcon"
              @click="handleAddEmail"
            >
              {{ typedEmail }}
            </Menu.Item>
          </Menu>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { Autocomplete, Chip, Menu, Pill, Skeleton } from '@dacr/library'
import { SmallGlobeIcon, ProfileIcon } from '@dacr/icons'

import {
  useSearchEntitiesLazyQuery,
  type Entity,
  type SearchEntitiesQuery,
} from '@/shared/graphql/legacy/generated'
import { useUserStore } from '@/shared/stores/user'
import { EMAIL_REGEX } from '@/shared/utils/string'
import { useFloatingMenu } from '@/shared/composables/useFloatingMenu'
import { useDevice } from '@/shared/composables/useDevice'

type SearchEntity = SearchEntitiesQuery['searchEntities']['entities'][number]

const props = withDefaults(
  defineProps<{
    modelValue: Entity[]
    disabled?: boolean
    showOwnEntity?: boolean
    size?: 'lg' | 'xl'
  }>(),
  { size: 'xl' }
)

const chipSize = computed(() => (props.size === 'xl' ? 'lg' : 'sm'))

const emit = defineEmits<{
  (e: 'update:modelValue', payload: Entity[]): void
}>()

const userStore = useUserStore()

const query = ref('')
const showResults = ref(false)

const { isMobile } = useDevice()

const { reference, floating, floatingStyles, transition } = useFloatingMenu(
  showResults,
  {
    flip: !isMobile.value,
  }
)

const variables = ref({
  query,
  showOwnEntity: props.showOwnEntity || false,
})

const { result, loading, load, fetchMore } =
  useSearchEntitiesLazyQuery(variables)

const users = computed<SearchEntity[]>(() => {
  const selectedIds = props.modelValue.map((entity) => entity.id)

  return (result.value?.searchEntities.entities ?? [])
    .filter((entity) => !selectedIds.includes(entity.id))
    .slice(0, 4)
})

const typedEmail = computed(() => query.value.trim())

const canAddEmail = computed(
  () =>
    !loading.value &&
    EMAIL_REGEX.test(typedEmail.value) &&
    !props.modelValue.some((entity) => entity.id === typedEmail.value)
)

const isEmail = (entity: Entity) => entity.id === entity.name

const isYou = (entity: SearchEntity) => entity.id === userStore.user?.id

const getSecondaryText = (entity: SearchEntity) => {
  if (entity.__typename === 'BasicUser') return entity.username

  return entity.owner?.__typename === 'BasicUser'
    ? `Managed by ${entity.owner.name}`
    : ''
}

const toEntity = (entity: SearchEntity): Entity => {
  if (entity.__typename === 'BasicUser') return entity

  return {
    __typename: 'Organization',
    id: entity.id,
    name: entity.name,
    avatarUrl: entity.avatarUrl,
    owner: entity.owner?.__typename === 'BasicUser' ? entity.owner : null,
  }
}

const addEntity = (entity: Entity) => {
  emit('update:modelValue', [...props.modelValue, entity])
}

const removeAt = (index: number) => {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index)
  )
}

const handleSelect = (entity: SearchEntity) => {
  addEntity(toEntity(entity))
  query.value = ''
  showResults.value = false
}

const addEmail = (value: string) => {
  const email = value.trim()

  if (!EMAIL_REGEX.test(email)) return false
  if (props.modelValue.some((entity) => entity.id === email)) return true

  addEntity({
    __typename: 'BasicUser',
    id: email,
    name: email,
    username: email,
    avatarUrl: '',
  })

  return true
}

const handleAddEmail = () => {
  if (!addEmail(query.value)) return

  query.value = ''
  showResults.value = false
}

const handleKeydown = (event: KeyboardEvent) => {
  if (![' ', ',', 'Enter'].includes(event.key)) return
  if (!addEmail(query.value)) return

  event.preventDefault()
  query.value = ''
  showResults.value = false
}

const handlePaste = (event: ClipboardEvent) => {
  const parts = (event.clipboardData?.getData('text') ?? '')
    .split(/[\s,]+/)
    .filter((part) => EMAIL_REGEX.test(part))

  if (!parts.length) return

  event.preventDefault()
  parts.forEach(addEmail)
  query.value = ''
  showResults.value = false
}

const handleFocus = () => {
  if (query.value.trim()) showResults.value = true
}

const fetchNextPage = () => {
  if (!result.value?.searchEntities.next || loading.value) return

  fetchMore({
    variables: {
      next: result.value.searchEntities.next,
    },
    updateQuery: (previousData, { fetchMoreResult: newData }) => {
      if (!newData) return previousData

      return {
        __typename: 'Query',
        searchEntities: {
          ...previousData.searchEntities,
          entities: [
            ...previousData.searchEntities.entities,
            ...newData.searchEntities.entities,
          ],
          next: newData.searchEntities.next,
        },
      }
    },
  })
}

const handleScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const scrollPos = element.clientHeight + element.scrollTop

  if (scrollPos / element.scrollHeight >= 0.5) fetchNextPage()
}

watch(query, (value) => {
  const trimmed = value.trim()
  showResults.value = !!trimmed

  if (trimmed) load()
})
</script>
